"use client";

import type { Session } from "@supabase/supabase-js";
import useSWR, { SWRConfiguration } from "swr";
import { CheckoutDetails } from "@features/checkout/types";
import { authorizedFetcher, fetcher, handle429s } from "@utils/fetchers";
import {
  useSupabaseSession,
  useLatestCookieValue,
  useHydrated,
} from "@utils/hooks";

type UseCheckoutDetailsOptions = {
  /** SWR configuration options */
  swrOptions?: SWRConfiguration<CheckoutDetails>;
  /** Whether to fetch checkout details. Defaults to true. */
  enabled?: boolean;
  /**
   * A server-verified session (e.g. from a page's SSR `session` prop), used
   * to seed the client-only session state this hook otherwise starts from
   * scratch. Without it, this hook can briefly key its SWR fetch off `null`
   * and hit the unauthenticated endpoint for an already-signed-in user
   * before `onAuthStateChange` resolves - see `useSupabaseSession`.
   */
  session?: Session | null;
};

/**
 * Centralized hook for fetching checkout details with proper session handling.
 *
 * This hook ensures:
 * 1. Consistent SWR key format based on session state
 * 2. Proper use of authorizedFetcher when session exists
 * 3. Content-language header is always sent (from NEXT_LOCALE cookie in fetchers)
 * 4. Proper hydration handling to avoid SSR issues
 *
 * @param options - Configuration options
 * @returns SWR response for checkout details
 */
export function useCheckoutDetails(options: UseCheckoutDetailsOptions = {}) {
  const { swrOptions, enabled = true, session: sessionProp } = options;

  const session = useSupabaseSession(sessionProp);
  const basketKey = useLatestCookieValue("ASP.NET_SessionId");
  const hydrated = useHydrated();

  // Determine the SWR key based on session state
  // When session exists, use [url, token] format for authorizedFetcher
  // When no session, use just url for regular fetcher
  // Only fetch when hydrated to avoid SSR issues with cookies
  const swrKey = !enabled
    ? null
    : session?.access_token && basketKey
    ? [`basket/${basketKey}/checkout`, session.access_token]
    : basketKey && hydrated && !session
    ? `basket/${basketKey}/checkout`
    : null;

  // Select the appropriate fetcher based on session state
  const selectedFetcher = session?.access_token ? authorizedFetcher : fetcher;

  // Merge onErrorRetry handlers to ensure 429 handling always runs
  const mergedOnErrorRetry: SWRConfiguration<CheckoutDetails>["onErrorRetry"] =
    (error, key, config, revalidate, revalidateOpts) => {
      // Always run handle429s first
      handle429s(error, key, config, revalidate, revalidateOpts);
      // Then run any custom onErrorRetry if provided
      swrOptions?.onErrorRetry?.(
        error,
        key,
        config,
        revalidate,
        revalidateOpts
      );
    };

  return useSWR<CheckoutDetails>(swrKey, selectedFetcher, {
    revalidateOnFocus: false,
    ...swrOptions,
    onErrorRetry: mergedOnErrorRetry,
  });
}
